<template>
  <div
    :style="{
      width,
      height,
      minHeight,
      minWidth,
      maxHeight,
      maxWidth,
      position: 'relative',
    }"
  >
    <v-progress-linear
      v-if="isLoading"
      indeterminate
      color="#78c2f5"
      height="6"
      style="position: absolute; top: 0; left: 0; width: 100%; z-index: 10"
    ></v-progress-linear>
    <div
      :ref="mapID"
      :id="mapID"
      :class="mapID"
      style="width: 100%; height: 100%"
    ></div>

    <!-- An overlay to disable user interaction with the map if enabled -->
    <div
      v-if="disabledInteraction"
      class="map-overlay"
      style="
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 10;
      "
    ></div>
  </div>
</template>
<script>
/*
  This component a reusable Google map canvas with pre build functions that allows you to interact with the map.
  In order to access the function/properties of the map you must assign a ref name in your parent view e.g <MapComponent ref="myMap" />
  Then you can call methods directly from your parent e.g. this.$refs.myMap.getZoom().

  General:
    - mapSetUp(): Initializes the map canvas
    - zoomInCustomAreas(areaIDs): Zooms in on the specified custom areas.
    - zoomInStandardAreas(areaIDs): Zooms in on the specified standard areas.
    - zoomInPolygon(polygon): Zooms in on the specified polygon.
    - getCenter(): Returns the current center position of the map.
    - getZoom(): Returns the current zoom level of the map.
    - killMap(): Destroys the map and clears all values.

  Area boundaries:
    - displayCustomAreas(customAreaIDs): Displays custom areas on the map. Accepts a single ID or an array of IDs (custom_areas table). The method will automatically fetch the geometries or just make them visible if they'be been fetched before.
    - hideCustomAreas(customAreaIDs): Hides custom areas on the map. Accepts a single ID or an array of IDs. This will hide the boundaries but still keep the geometries in memory for future use.
    - clearCustomAreas(): Bins all custom areas and their geometries.

    - displayStandardAreas(areaIDs): Displays standard areas on the map. Accepts a single ID or an array of IDs (areas table). The method will automatically fetch the geometries or just make them visible if they'be been fetched before.
    - hideStandardAreas(areaIDs): Hides standard areas on the map. Accepts a single ID or an array of IDs. This will hide the boundaries but still keep the geometries in memory for future use.
    - clearStandardAreas(): Clears all standard areas and their geometries from the map.

    - changeColourForAreas(colour): Changes the colour of boundaries of all plotted areas onto the map.
    - clearAllAreas(): Clears all plotted areas custom/standard and their geometries

  PROPS:
    - initialiseMapOnMount: Whether to initialize the map when the component is mounted. Handy to turn it off when the map is in dialog box on your page and you need to manually initialize it when the dialog is opened.
    - showMapControls: Whether to show map controls. Type Boolean|Object. If object, you can customize the controls (e.g. { zoomControl: false, streetViewControl: true } )
    - width: The width of the map container.
    - height: The height of the map container.
    - zoomChange: Callback function that is fired when zoom on the map changes.
    - centerChange: Callback function that is fired when center on the map changes.
    - loading: By default loading bar is always displayed during geometry fetches, but if you need to have it at other times you can bind this prop to control it from the parent.
    - disabledInteraction: disables zooming and panning by user control (zooming functions will still work).

  OTHER:
    - satelliteView: a dynamic toggle for satellite view on the map.

*/

/* global google */
import { loadGoogleMaps } from "@/mixins/LoadGoogleMaps";
import { systemMessages } from "@/mixins/SystemMessages";
import { toRaw } from "vue";

export default {
  name: "MapComponent",
  mixins: [systemMessages],
  props: {
    initialiseMapOnMount: {
      type: Boolean,
      default: true,
    },
    showMapControls: {
      type: [Object, Boolean],
      required: false,
      default: true,
    },
    width: {
      type: String,
      default: "100%",
      required: false,
    },
    height: {
      type: String,
      default: "400px",
      required: false,
    },
    minHeight: {
      type: String,
      default: "",
      required: false,
    },
    maxHeight: {
      type: String,
      default: "",
      required: false,
    },
    minWidth: {
      type: String,
      default: "",
      required: false,
    },
    maxWidth: {
      type: String,
      default: "",
      required: false,
    },
    zoomChange: {
      type: [Function, null],
      default: null,
      required: false,
    },
    centerChange: {
      type: [Function, null],
      default: null,
      required: false,
    },
    mapID: {
      type: [String, Number],
      default: "mapCanvas",
      required: false,
    },
    loading: {
      type: Boolean,
      default: false,
      required: false,
    },
    roadMapBoundaryStyle: {
      Object,
      default: () => {
        return {};
      },
      required: false,
    },
    satelliteBoundaryStyle: {
      Object,
      default: () => {
        return {};
      },
      required: false,
    },
    disabledInteraction: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  computed: {
    isLoading: {
      get() {
        return this.localLoading || this.loading;
      },
      set(val) {
        this.$emit("update:loading", val);
      },
    },
  },
  data() {
    return {
      map: null,
      zoom: null,
      center: {
        lat: null,
        lng: null,
      },
      customAreaPolygons: [],
      standardAreaPolygons: [],
      abortSAControllers: [],
      abortCAControllers: [],
      satelliteView: false,
      localLoading: false, // loading state when fetching geometries
    };
  },
  async mounted() {
    this.zoom = this.$store.state.config.siteConfig.initialMapZoom.zoom;
    this.center.lat = this.$store.state.config.siteConfig.initialMapZoom.lat;
    this.center.lng = this.$store.state.config.siteConfig.initialMapZoom.lng;

    // if local vm then it's handy to rebuild map on every change rather then reloading page manually
    this.$nextTick(() => {
      if (
        this.initialiseMapOnMount ||
        process.env.VUE_APP_ENVIRONMENT === "dev"
      ) {
        this.mapSetUp();
      }
    });
  },
  methods: {
    async mapSetUp() {
      return new Promise((resolve) => {
        loadGoogleMaps(
          this.center,
          this.zoom,
          this.mapID,
          this.showMapControls,
        ).then((loadedMap) => {
          this.map = loadedMap;

          // Add event listeners for zoom and center changes
          if (this.zoomChange) {
            this.map.addListener(
              "zoom_changed",
              () => {
                this.zoomChange();
              },
              {
                passive: true,
              },
            );
          }
          if (this.centerChange) {
            this.map.addListener(
              "center_changed",
              () => {
                this.centerChange();
              },
              { passive: true },
            );
          }

          this.map.addListener("maptypeid_changed", () => {
            this.satelliteView = toRaw(this.map).getMapTypeId() === "hybrid";
          });

          setTimeout(() => {
            resolve();
          }, 300);
        });
      });
    },
    getZoom() {
      return this.map?.getZoom();
    },
    zoomInCustomAreas(areaIDs) {
      if (!areaIDs) return;
      areaIDs = Array.isArray(areaIDs) ? areaIDs : [areaIDs];
      if (!areaIDs?.length) return;

      // only keep the custom polygons matching the IDs
      const toZoom = this.customAreaPolygons.filter((p) =>
        areaIDs.includes(p.custom_area_id),
      );
      this.zoomInPolygons(toZoom);
    },
    zoomInStandardAreas(areaIDs) {
      if (!areaIDs) return;
      areaIDs = Array.isArray(areaIDs) ? areaIDs : [areaIDs];
      if (!areaIDs?.length) return;

      // only keep the standard polygons matching the IDs
      const toZoom = this.standardAreaPolygons.filter((p) =>
        areaIDs.includes(p.area_id),
      );
      this.zoomInPolygons(toZoom);
    },
    zoomInPolygons(polygons) {
      if (!polygons) return;
      polygons = Array.isArray(polygons) ? polygons : [polygons];
      if (!polygons?.length) return;

      const bounds = new google.maps.LatLngBounds();
      polygons.forEach((polygon) => {
        polygon.getPath().forEach((latLng) => bounds.extend(latLng));
      });
      this.map.fitBounds(bounds);
    },
    /**
     * Wait until the map is initialized (this.map != null) or timeout.
     */
    async waitForMapInit(timeout = 3000) {
      const start = Date.now();
      while (!this.map && Date.now() - start < timeout) {
        // poll every 50ms
        // eslint-disable-next-line no-await-in-loop
        await new Promise((res) => setTimeout(res, 50));
      }

      // if map is still not initialized after timeout, log an error
      if (!this.map) {
        console.error(`Map initialization timed out after ${timeout}ms`);
      }
    },
    /*
     * Display a custom area on the map
     * First see if we already have fetched its geometries before, otherwise make an API call.
     */
    async displayCustomAreas(customAreaIDs) {
      // ensure map is initialized before executing
      await this.waitForMapInit();
      customAreaIDs = Array.isArray(customAreaIDs)
        ? customAreaIDs
        : [customAreaIDs];

      // if geometries are already in place, then just make it visible
      let fetchedAreaIDs = [];
      this.customAreaPolygons.forEach((polygon) => {
        if (customAreaIDs.includes(polygon.custom_area_id)) {
          toRaw(polygon).setVisible(true);

          fetchedAreaIDs.push(polygon.custom_area_id);
        }
      });

      // areas not yet fetched
      if (customAreaIDs.filter((id) => !fetchedAreaIDs.includes(id)).length) {
        const feature = await this.fetchCustomAreaGeometries(customAreaIDs);
        if (feature) {
          // upload it onto the map
          for (const f of feature) {
            const geometry = f.geometry;
            const geometryType = geometry.type;
            const areaId = f.properties.custom_area_id;
            const { Polygon } = await google.maps.importLibrary("maps");

            if (geometryType === "Polygon") {
              // Polygon with possible holes
              const googleMapsPaths = geometry.coordinates.map((ring) =>
                ring.map((coord) => {
                  return { lat: coord[1], lng: coord[0] };
                }),
              );
              this.customAreaPolygons.push(
                new Polygon({
                  map: toRaw(this.map),
                  paths: googleMapsPaths,
                  fillOpacity: this.getStylePropertyForPolygon("fillOpacity"),
                  strokeWeight: this.getStylePropertyForPolygon("strokeWeight"),
                  strokeColor: this.getStylePropertyForPolygon("strokeColor"),
                  zIndex: this.getStylePropertyForPolygon("zIndex"),
                  clickable: false,
                  custom_area_id: areaId,
                }),
              );
            } else if (geometryType === "MultiPolygon") {
              // MultiPolygon
              const multiCoordinates = geometry.coordinates;
              multiCoordinates.forEach((coords) => {
                const googleMapsPaths = coords.map((coord) => {
                  return coord.map((subCoord) => {
                    return { lat: subCoord[1], lng: subCoord[0] };
                  });
                });
                this.customAreaPolygons.push(
                  new Polygon({
                    map: toRaw(this.map),
                    paths: googleMapsPaths,
                    fillOpacity: this.getStylePropertyForPolygon("fillOpacity"),
                    strokeWeight:
                      this.getStylePropertyForPolygon("strokeWeight"),
                    strokeColor: this.getStylePropertyForPolygon("strokeColor"),
                    zIndex: this.getStylePropertyForPolygon("zIndex"),
                    clickable: false,
                    custom_area_id: areaId,
                  }),
                );
              });
            }
          }
        }
      }
    },
    getStylePropertyForPolygon(property) {
      const defaultStyle = {
        fillOpacity: 0,
        strokeWeight: 3,
        strokeColor: this.satelliteView ? "#FF0000" : "#000000",
        strokeOpacity: 1,
        strokePosition: "",
        fillColor: "",
        zIndex: 10,
      };

      const userCustomPreference = this.satelliteView
        ? "satelliteBoundaryStyle"
        : "roadMapBoundaryStyle";

      const style =
        this[userCustomPreference][property] ?? defaultStyle[property];

      if (typeof style === "undefined") {
        console.error("Invalid polygon style");
        return "";
      }

      return style;
    },
    async hideCustomAreas(customAreaIDs) {
      // ensure map is initialized before executing
      await this.waitForMapInit();
      if (Number.isInteger(customAreaIDs)) {
        customAreaIDs = [customAreaIDs];
      }

      if (customAreaIDs) {
        this.customAreaPolygons.forEach((polygon) => {
          if (customAreaIDs.includes(polygon.custom_area_id)) {
            toRaw(polygon).setVisible(false);
          }
        });
      }
    },
    async fetchCustomAreaGeometries(customAreaIDs) {
      // cancel all in-flight requests then fetch new geometries
      if (this.abortCAControllers.length) {
        this.abortCAControllers.forEach((c) => c.abort());
        this.abortCAControllers = [];
      }

      // create and store controller for this fetch so we could cancel the request if a new one is made before the previous one is complete
      const controller = new AbortController();
      this.abortCAControllers.push(controller);
      const signal = controller.signal;

      this.localLoading = true;
      try {
        const { data } = await this.$axios.post(
          "custom-areas-polygon",
          customAreaIDs,
          { signal },
        );

        return data.features;
      } catch (error) {
        // ignore aborts, show other errors
        if (error.name !== "CanceledError") {
          this.errorMsg({
            title: "Failed to fetch geometries",
            message: error.response?.data.message,
          });
        }
      } finally {
        // remove this controller from pending list
        this.abortCAControllers = this.abortCAControllers.filter(
          (c) => c !== controller,
        );
        this.localLoading = false;
      }
    },
    async fetchStandardAreaGeometries(areaIDs) {
      // cancel all in-flight requests then fetch new geometries
      if (this.abortSAControllers.length) {
        this.abortSAControllers.forEach((c) => c.abort());
        this.abortSAControllers = [];
      }

      // create and store controller for this fetch so we could cancel the request if a new one is made before the previous one is complete
      const controller = new AbortController();
      this.abortSAControllers.push(controller);
      const signal = controller.signal;

      this.localLoading = true;
      try {
        const { data } = await this.$axios.post(
          "standard-areas-polygon",
          areaIDs,
          { signal },
        );

        return data.features;
      } catch (error) {
        // ignore aborts, show other errors
        if (error.name !== "CanceledError") {
          this.errorMsg({
            title: "Failed to fetch geometries",
            message: error.response?.data.message,
          });
        }
      } finally {
        // remove this controller from pending list
        this.abortSAControllers = this.abortSAControllers.filter(
          (c) => c !== controller,
        );
        this.localLoading = false;
      }
    },
    async displayStandardAreas(areaIDs) {
      // ensure map is initialized before executing
      await this.waitForMapInit();
      areaIDs = Array.isArray(areaIDs) ? areaIDs : [areaIDs];

      // if geometries are already in place, then just make it visible
      let fetchedAreaIDs = [];
      this.standardAreaPolygons.forEach((polygon) => {
        if (areaIDs.includes(polygon.area_id)) {
          toRaw(polygon).setVisible(true);

          fetchedAreaIDs.push(polygon.area_id);
        }
      });

      // areas not yet fetched
      if (areaIDs.filter((id) => !fetchedAreaIDs.includes(id)).length) {
        const feature = await this.fetchStandardAreaGeometries(areaIDs);
        if (feature) {
          // upload it onto the map
          for (const f of feature) {
            const geometry = f.geometry;
            const geometryType = geometry.type;
            const id = f.properties.area_id;
            const { Polygon } = await google.maps.importLibrary("maps");

            if (geometryType === "Polygon") {
              // Polygon with possible holes
              const googleMapsPaths = geometry.coordinates.map((ring) =>
                ring.map((coord) => {
                  return { lat: coord[1], lng: coord[0] };
                }),
              );
              this.standardAreaPolygons.push(
                new Polygon({
                  map: this.map,
                  paths: googleMapsPaths,
                  fillOpacity: this.getStylePropertyForPolygon("fillOpacity"),
                  strokeWeight: this.getStylePropertyForPolygon("strokeWeight"),
                  strokeColor: this.getStylePropertyForPolygon("strokeColor"),
                  zIndex: this.getStylePropertyForPolygon("zIndex"),
                  clickable: false,
                  area_id: id,
                }),
              );
            } else if (geometryType === "MultiPolygon") {
              // MultiPolygon
              const multiCoordinates = geometry.coordinates;
              multiCoordinates.forEach((coords) => {
                const googleMapsPaths = coords.map((coord) => {
                  return coord.map((subCoord) => {
                    return { lat: subCoord[1], lng: subCoord[0] };
                  });
                });
                this.standardAreaPolygons.push(
                  new Polygon({
                    map: this.map,
                    paths: googleMapsPaths,
                    fillOpacity: this.getStylePropertyForPolygon("fillOpacity"),
                    strokeWeight:
                      this.getStylePropertyForPolygon("strokeWeight"),
                    strokeColor: this.getStylePropertyForPolygon("strokeColor"),
                    zIndex: this.getStylePropertyForPolygon("zIndex"),
                    clickable: false,
                    area_id: id,
                  }),
                );
              });
            }
          }
        }
      }
    },
    async hideStandardAreas(areaIDs) {
      // ensure map is initialized before executing
      await this.waitForMapInit();
      if (Number.isInteger(areaIDs)) {
        areaIDs = [areaIDs];
      }

      if (areaIDs) {
        this.standardAreaPolygons.forEach((polygon) => {
          if (areaIDs.includes(polygon.area_id)) {
            toRaw(polygon).setVisible(false);
          }
        });
      }
    },
    /*
      All values that must be cleared on map destroy must be added to this function
    */
    async killMap() {
      return new Promise((resolve) => {
        this.customAreaPolygons.forEach((polygon) => {
          toRaw(polygon).setMap(null);
        });
        this.standardAreaPolygons.forEach((polygon) => {
          toRaw(polygon).setMap(null);
        });

        this.customAreaPolygons = [];
        this.standardAreaPolygons = [];
        this.map = null;

        // add other stuff to be cleared

        resolve();
      });
    },
    /*
      Clear all custom areas and its geometries. The displayCustomAreas would need to fetch geometries of the cleared areas again.
    */
    async clearCustomAreas() {
      return new Promise((resolve) => {
        this.customAreaPolygons.forEach((polygon) => {
          toRaw(polygon).setMap(null);
        });
        this.customAreaPolygons = [];
        resolve();
      });
    },
    /*
      Clear all standard areas and its geometries. The displayStandardAreass would need to fetch geometries of the cleared areas again.
    */
    async clearStandardAreas() {
      return new Promise((resolve) => {
        this.standardAreaPolygons.forEach((polygon) => {
          toRaw(polygon).setMap(null);
        });
        this.standardAreaPolygons = [];
        resolve();
      });
    },
    async clearAllAreas() {
      return new Promise((resolve) => {
        this.customAreaPolygons.forEach((polygon) => {
          toRaw(polygon).setMap(null);
        });
        this.standardAreaPolygons.forEach((polygon) => {
          toRaw(polygon).setMap(null);
        });
        this.customAreaPolygons = [];
        this.standardAreaPolygons = [];

        resolve();
      });
    },
    /*
      Change colour of boundaries of plotted areas onto the map
    */
    changeColourForAreas(colour) {
      // custom areas
      this.customAreaPolygons.forEach((polygon) => {
        polygon.setOptions({
          strokeColor: colour,
        });
      });
      // standard areas
      this.standardAreaPolygons.forEach((polygon) => {
        polygon.setOptions({
          strokeColor: colour,
        });
      });
    },
  },
  watch: {
    /*
      When the satellite view is toggled, change the colour of displayed areas
    */
    satelliteView: {
      handler(isSatelliteView) {
        if (toRaw(this.map)) {
          toRaw(this.map).setMapTypeId(isSatelliteView ? "hybrid" : "roadmap");

          // Change the colour of custom areas based on the view
          const colour = isSatelliteView ? "#FF0000" : "#000000";
          this.changeColourForAreas(colour);
        }
      },
    },
  },
};
</script>
